import {isUndefined} from 'lodash';
import {connect} from 'react-redux';

import {LoadedMessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {
  InterfaceEventsEnum,
  PopoutLocationsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {
  CellsContext,
  CellsContextTypesEnum,
} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {NewPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {delayAsync} from '../../../../../helpers/asyncHelpers';
import {emlFileName} from '../../../../../helpers/models/messageHelpers';
import {downloadFileAsync} from '../../../../../services/browser';
import {deleteMessageTask} from '../../../../../services/tasks/deleteMessageTask';
import {destroyMessageTask} from '../../../../../services/tasks/destroyMessageTask';
import {splitConversationTask} from '../../../../../services/tasks/splitConversationTask';
import {AppState} from '../../../../../store/appState';
import {getConversations} from '../../../../../store/data/dataSelectors';
import {getIsUserVisitor} from '../../../../../store/data/teammates/teammatesSelectors';
import {removePanelAction} from '../../../../../store/panels/panelsActions';
import {makeGetIsMessagePopup} from '../../../../../store/panels/panelsSelectors';
import {
  popoutLinkedConversationViewerAction,
  popoutLinkedDiscussionViewerAction,
} from '../../../../../store/panels/viewer/viewerPanelConversationsActions';
import {navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {MessageViewerMore} from './messageViewerMore';

/*
 * Props.
 */

interface MessageViewerMoreContainerProps {
  panelId: NewPanelId;
  cellsContext: CellsContext;
  message: LoadedMessageModel;
  activityId: number | undefined;
  onPopoutClick: () => void;
  onRequestClose?: () => void;
}

/*
 * Selectors.
 */

function getConversationId(_state: AppState, props: MessageViewerMoreContainerProps) {
  return props.message.conversationId;
}

function makeGetConversation() {
  return createSelector(
    [getConversationId, getConversations],
    (conversationId, conversations) => conversations[conversationId],
  );
}

function getPanelId(_state: AppState, props: MessageViewerMoreContainerProps) {
  return props.panelId;
}

function makeGetInTeammateContext() {
  return createSelector([getCellsContext], (cellsContext) => {
    if (isUndefined(cellsContext)) {
      return false;
    }

    return cellsContext.type === CellsContextTypesEnum.TEAMMATE && !cellsContext.isDelegation;
  });
}

function getCellsContext(_state: AppState, props: MessageViewerMoreContainerProps) {
  return props.cellsContext;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MessageViewerMoreContainerProps>()(() => ({
  isUserVisitor: getIsUserVisitor,
  conversation: makeGetConversation(),
  isMessagePopup: makeGetIsMessagePopup(getPanelId),
  isInTeammateContext: makeGetInTeammateContext(),
}));

const mapDispatchProps = makeMapDispatchToProps<MessageViewerMoreContainerProps>()(() => ({
  splitConversationTask,
  destroyMessageTask,
  deleteMessageTask,
  navigateToLinkAction,
  removePanel: removePanelAction,
  popoutLinkedConversationViewer: popoutLinkedConversationViewerAction,
  popoutLinkedDiscussionViewer: popoutLinkedDiscussionViewerAction,
}));

export const MessageViewerMoreContainer = connect(
  mapStateToProps,
  mapDispatchProps,
  (stateProps, dispatchProps, ownProps) => {
    const {cellsContext, message, activityId, panelId} = ownProps;
    const {conversation, isMessagePopup} = stateProps;

    const onSaveEmail = async () => {
      trackInterfaceEvent(InterfaceEventsEnum.SAVE_EML, message.id);

      try {
        const filename = emlFileName(message);
        await downloadFileAsync(`${message.messageUrl}/eml`, filename, {shouldPromptForPath: true});
      } catch (error) {
        reportToBugsnag(error);
      }
    };

    const onSplitClick = async () => {
      if (!conversation) {
        return;
      }

      trackInterfaceEvent(InterfaceEventsEnum.SPLIT_CONVERSATION, conversation.id);

      const destinationConversation = await dispatchProps.splitConversationTask(conversation.id, message);
      if (!destinationConversation) {
        return;
      }

      // Navigate to the newly created conversation.
      const newUrl = `${cellsContext.path}/${destinationConversation.id}`;

      // There is a delay between spliting and creating a new conversation and loading it into P2.
      // This delay is used to give more time for the conversation to be ready to load.
      await delayAsync(1100);

      if (isMessagePopup && !isUndefined(panelId)) {
        dispatchProps.removePanel(ownProps.panelId);
      }

      dispatchProps.navigateToLinkAction(newUrl);
    };

    const onDestroyMessageClick = () => {
      trackInterfaceEvent(InterfaceEventsEnum.DESTROY_MESSAGE, message.id);

      if (activityId) {
        dispatchProps.destroyMessageTask(activityId);
      } else {
        dispatchProps.deleteMessageTask(message.uid);
      }
    };

    const onPopoutLinkedConversationClick = (
      trackCreateLinkedConversationsSuccess?: (arg: number) => void,
    ) => {
      if (!conversation) {
        return;
      }

      dispatchProps.popoutLinkedConversationViewer({
        originalLinkedConversationId: conversation.id,
        selectedInboxId: cellsContext.inboxId,
        location: PopoutLocationsEnum.MORE_MENU,
        trackSuccess: trackCreateLinkedConversationsSuccess,
      });
    };

    const onPopoutLinkedDiscussionClick = () => {
      if (!conversation) {
        return;
      }

      dispatchProps.popoutLinkedDiscussionViewer({
        originalLinkedConversationId: conversation.id,
      });
    };

    // Removing onRequestClose since it's not officially expected by messageViewerMoreContainer,
    // yet it can be spread at runtime and it can create side effects when closing the Dropdowns component.
    const {onRequestClose, ...onRequestCloseLessProps} = ownProps;

    return {
      ...onRequestCloseLessProps,
      ...stateProps,
      onSaveEmail,
      onSplitClick,
      onDestroyMessageClick,
      onPopoutLinkedConversationClick,
      onPopoutLinkedDiscussionClick,
    };
  },
)(MessageViewerMore);
